'use client';

import { useEffect, useState } from 'react';

import { doc, firestore, onSnapshot } from '@ps/functions/firebase/firestore';
import { useCurrentUserId } from '@ps/ui/contexts/auth';

/**
 * Reactive "is the current user following this creator?" — subscribes to the
 * Firestore doc `profiles/{userId}/followings/{creatorId}`, exactly the source
 * PopshopRN's `isFollowing$` reads (and the same doc the `access-guard` follow
 * check watches). Returns `undefined` while resolving / for anonymous users,
 * then a live boolean; a follow (or unfollow) writes that doc and the value
 * flips without a refetch.
 */
export function useIsFollowing(creatorId: string): boolean | undefined {
  const userId = useCurrentUserId();
  const [isFollowing, setIsFollowing] = useState<boolean>();

  useEffect(() => {
    if (!userId || !creatorId) {
      setIsFollowing(userId === undefined ? undefined : false);
      return;
    }
    const ref = doc(firestore(), 'profiles', userId, 'followings', creatorId);
    const unsubscribe = onSnapshot(
      ref,
      snap => setIsFollowing(snap.exists()),
      () => setIsFollowing(false),
    );
    return unsubscribe;
  }, [userId, creatorId]);

  return isFollowing;
}
